import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop'
import { MatIconModule } from '@angular/material/icon'
import { TranslateModule } from '@ngx-translate/core'
import {
  WidgetModel,
  WidgetType,
} from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import { GbPresenterToolsElevationComponent } from '../gb-presenter-tools-elevation/gb-presenter-tools-elevation.component'
import { GbPresenterColorResolverService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-color-resolver.service'

export interface LayerItem {
  uuid: string
  widgetType: WidgetType
  z: number
  label: string
  isLabelKey: boolean
  widget: WidgetModel
}

@Component({
  selector: 'gb-presenter-layers',
  imports: [CommonModule, DragDropModule, MatIconModule, TranslateModule, GbPresenterToolsElevationComponent],
  templateUrl: './gb-presenter-layers.component.html',
  styleUrl: './gb-presenter-layers.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterLayersComponent {
  @Input({ required: true }) layers: LayerItem[] | null = null
  @Input() selectedWidget: WidgetModel | null = null
  @Input() backgroundColor: string | null = null
  @Input() backgroundImage: string | null = null

  @Output() selectLayer = new EventEmitter<LayerItem>()
  @Output() selectBackground = new EventEmitter<void>()
  @Output() reorderLayers = new EventEmitter<LayerItem[]>()

  @Output() bringToFront = new EventEmitter<void>()
  @Output() bringForward = new EventEmitter<void>()
  @Output() sendBackward = new EventEmitter<void>()
  @Output() sendToBack = new EventEmitter<void>()

  get resolvedBackgroundColor(): string {
    return this.colorResolver.resolve(this.backgroundColor) || '#ffffff'
  }

  get notificationLayers(): LayerItem[] {
    return (this.layers ?? []).filter((l) => l.widgetType === WidgetType.NOTIFICATION)
  }

  get draggableLayers(): LayerItem[] {
    return (this.layers ?? []).filter((l) => l.widgetType !== WidgetType.NOTIFICATION)
  }

  constructor(private colorResolver: GbPresenterColorResolverService) {}

  onBringToFront(): void {
    this.bringToFront.emit()
  }

  onBringForward(): void {
    this.bringForward.emit()
  }

  onSendBackward(): void {
    this.sendBackward.emit()
  }

  onSendToBack(): void {
    this.sendToBack.emit()
  }

  onSelectLayer(layer: LayerItem): void {
    this.selectLayer.emit(layer)
  }

  onSelectBackground(): void {
    this.selectBackground.emit()
  }

  onDrop(event: CdkDragDrop<LayerItem[]>): void {
    if (!this.layers) return

    const pinnedTop = this.notificationLayers
    const nextDraggable = [...this.draggableLayers]
    moveItemInArray(nextDraggable, event.previousIndex, event.currentIndex)
    this.reorderLayers.emit([...pinnedTop, ...nextDraggable])
  }

  trackByUuid(_index: number, item: LayerItem): string {
    return item.uuid
  }
}
